import React, {useState, Fragment, useEffect, useCallback, useMemo} from 'react';
import {Form, Input, Select, Row, Col, Icon, Tooltip, InputNumber, Button, Switch} from 'antd';
import {FormattedMessage} from 'react-intl';
import debounce from 'lodash/debounce';
import ProvinceSingleSelection from "@components/provinces/selection/SingleSelection";
import VideoSelection from "@components/video-contents/Selection";
import DatePicker from 'react-datepicker2';
import {SelectionMode} from "@models";
import PersonSelection from '@components/people/Selection';
import PersonRoleSelection from "@components/person-roles/PersonRoleSelection";
import CategorySelection from "@components/categories/Selection";
import TagsSelection from "@components/tags/Selection";
import ChannelsSelection from "@components/channels/Selection";
import SuppliersSelection from "@components/supplier/Selection";
import MobileInput from "../custom/MobileInput";


const {Option} = Select;

export const enum SearchInputType {
  "TEXT" = "text",
  "NUMBER" = "number",
  "MOBILE" = "mobile",
  "SELECT" = "select",
  "DATE" = "date",
  "PROVINCE" = "province",
  "VIDEO" = "video",
  "PERSON" = "person",
  "PERSON_ROLE" = "person_role",
  "ROLE" = "role",
  "CATEGORY" = "category",
  "TAG" = "tag",
  "CHANNEL" = "channel",
  "SUPPLIER" = "supplier",
  "SWITCH" = "switch"
}

export interface SearchInputOption {
  value: string;
  label: string;
}


export interface SearchInput {
  name: string;
  label: string;
  type: SearchInputType
  options?: SearchInputOption[]
  onChangeFilter?: (filter: object) => any;
}


interface SearchProps {
  inputs: SearchInput[];
  search: (filter: object) => any;
}

/**
 * This is a simple search input which handle search on Enter
 * @param props
 * @constructor
 */
const Search: React.FC<SearchProps> = props => {
  const {inputs, search, searchOnChange = true, hasClearButton = true, onChangeFilter, form} = props;
  const {getFieldDecorator} = form;
  const [filter, setFilter] = useState({});
  const delay = useMemo(() => searchOnChange ? 800 : 0, [])
  /**
   * This is trick ro force re-render cleared form
   * form.setFieldsValue(obj); // does not work ..because we handle oChange manually
   */
  const [clearDate, setClearDate] = useState(new Date().getTime());

  useEffect(
    () => {
      onChangeFilter && onChangeFilter(filter);
    },
    [filter]
  )

  const searchFn = (obj) => {
    const newFilterObject = {
      ...filter,
      ...obj
    };

    setFilter(newFilterObject);

    if (searchOnChange) {
      search(newFilterObject)
    }
  };

  const handleClickSearchBtn = () => {
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        search(filter)
      }
    });
  };

  const onChangeTextInput = debounce((name, value) => {
    const trimedValue = typeof value === "string" ? value.trim() : value;
    const valueInFilter = filter[name] || "";
    form.validateFieldsAndScroll()
    if (valueInFilter !== trimedValue) {
      searchFn({
        [name]: trimedValue
      });
    }
  }, delay);

  const onChangeSelectInput = (name, value) => {
    searchFn({
      [name]: value
    });
  };

  const onChangeCustomSelect = (input, returnValue) => {
    searchFn({
      [input.name]: !input.multiple ? returnValue.id : returnValue.selectedItems.map(opt => opt.id)
    });
  };

  const onChangeDate = (name, date) => {
    searchFn({
      [name]: new Date(date.format("YYYY-MM-DD")).toISOString()
    });
  };


  //TODO: resetForm in ant
  const clearSearch = () => {
    if (Object.values(filter).length > 0) {
      let obj = {};
      Object.keys(filter).forEach(key => obj = {
        ...obj,
        [key]: null
      });
      setFilter({});
      setClearDate(new Date().getTime());
      search({});
    }
  };

  const getInput = (input) => {
    const {InputProps = {}, component: Component} = input;


    if (Component) {
      return (
        <Component {...InputProps} onChange={(item) => onChangeCustomSelect(input, item)}/>
      )
    } else {
      switch (input.type) {
        case SearchInputType.TEXT:
          return (
            <Input {...InputProps} onChange={(e) => onChangeTextInput(input.name, e.target.value)}/>
          );

        case SearchInputType.MOBILE:
          return (
            <MobileInput  {...InputProps} onChange={(e) => onChangeTextInput(input.name, e.target.value)}/>
          );

        case SearchInputType.NUMBER:
          return (
            <InputNumber {...InputProps} onChange={(value) => onChangeTextInput(input.name, value)}/>
          );

        case SearchInputType.SWITCH:
          return (
            <Switch {...InputProps} onChange={(value) => onChangeTextInput(input.name, value)}/>
          );

        case SearchInputType.SELECT:
          return (
            <Select
              {...InputProps}
              onChange={(value) => onChangeSelectInput(input.name, value)}>
              {input.options.map((opt) => (
                <Option key={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          );

        case SearchInputType.PROVINCE:
          return (
            <ProvinceSingleSelection
              mode={SelectionMode.DEFAULT}
              onChange={(province) => onChangeCustomSelect(input, province)}/>
          );

        case SearchInputType.CHANNEL:
          return (
            <ChannelsSelection
              mode={SelectionMode.DEFAULT}
              onChange={(channel) => onChangeCustomSelect(input, channel)}/>
          );

        case SearchInputType.SUPPLIER:
          return (
            <SuppliersSelection
              mode={SelectionMode.DEFAULT}
              onChange={(channel) => onChangeCustomSelect(input, channel)}/>
          );

        case SearchInputType.VIDEO:
          return (
            <VideoSelection
              mode={input.multiple ? SelectionMode.MULTIPLE : SelectionMode.DEFAULT}
              onChange={(video) => onChangeCustomSelect(input, video)}/>
          );

        case SearchInputType.DATE:
          return (
            <DatePicker
              {...InputProps}
              isGregorian={false}
              timePicker={false}
              onChange={(date) => {
                InputProps.onChange && InputProps.onChange(date);
                onChangeDate(input.name, date)
              }}/>
          );

        case SearchInputType.PERSON:
          return (
            <PersonSelection
              mode={SelectionMode.DEFAULT}
              onChange={(person) => onChangeCustomSelect(input, person)}/>
          );

        case SearchInputType.PERSON_ROLE:
        case SearchInputType.ROLE:
          return (
            <PersonRoleSelection
              mode={SelectionMode.DEFAULT}
              onChange={(personRole) => onChangeCustomSelect(input, personRole)}/>
          );

        case SearchInputType.CATEGORY:
          return (
            <CategorySelection
              mode={input.multiple ? SelectionMode.MULTIPLE : SelectionMode.MULTIPLE}
              onChange={(category) => onChangeCustomSelect(input, category)}/>
          );

        case SearchInputType.TAG:
          return (
            <TagsSelection
              mode={input.multiple ? SelectionMode.MULTIPLE : SelectionMode.MULTIPLE}
              onChange={(tag) => onChangeCustomSelect(input, tag)}/>
          );

        default:
          return null
      }
    }
  };

  return (
    <div className={`search-form`}>
      <Form key={clearDate}>
        <Row gutter={24}>
          {inputs.map(formItem => (
            <Col span={formItem.size} key={formItem.name}>
              <Form.Item label={formItem.label}>
                {getFieldDecorator(formItem.name, {
                  initialValue: filter[formItem.name],
                  rules: formItem.validationRules || []
                })(
                  <Fragment>
                    {getInput(formItem)}
                  </Fragment>
                )}
              </Form.Item>
            </Col>
          ))}
        </Row>
      </Form>

      {/*{!searchOnChange && (*/}
      {/*<Button*/}
      {/*type={"primary"}*/}
      {/*size={'small'}*/}
      {/*style={{position:"absolute", left: "5px", top:"24px"}}*/}
      {/*disabled={Object.values(filter).length > 0 ? false : true}*/}
      {/*onClick={handleClickSearchBtn}>*/}
      {/*<FormattedMessage id="app.search" defaultMessage={`Search`}/>*/}
      {/*</Button>*/}
      {/*)}*/}

      <div style={{position: "absolute", left: "15px", top: "30px"}}>
        {!searchOnChange && (
          <Tooltip
            placement="top"
            title={
              <FormattedMessage id="app.search" defaultMessage={`Search`}/>
            }>
            <Button
              size={'small'}
              type={'primary'}
              disabled={Object.values(filter).length > 0 ? false : true}
              onClick={handleClickSearchBtn}
              icon={"search"}/>
          </Tooltip>
        )}
        {"  "}
        {hasClearButton && (
          <Tooltip
            placement="top"
            title={
              <FormattedMessage id="app.clear_filter" defaultMessage={`Clear Filter`}/>
            }>
            <Button
              size={'small'}
              type={'danger'}
              disabled={Object.values(filter).length > 0 ? false : true}
              onClick={clearSearch}
              icon={"close"}/>
          </Tooltip>
        )}
      </div>

    </div>
  )

};


export default Form.create({
  name: "search-form"
})(Search);